/**
 * useTraining Hook (Feature #56)
 *
 * React hook for managing training simulator state: scenarios, sessions, scoring.
 * Includes localStorage persistence to survive page refreshes.
 *
 * @module useTraining
 */

'use client';

import { useCallback, useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';

import { apiClient } from '@/lib/api-client';

import { loadFromStorage, saveToStorage, clearFromStorage } from '../utils/persisted-state.utils';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Local storage key for persisting active training session */
const STORAGE_KEY_SESSION = 'axon-training-session';
/** Local storage key for persisting last score */
const STORAGE_KEY_LAST_SCORE = 'axon-training-last-score';
/** Local storage key for persisting elapsed time */
const STORAGE_KEY_ELAPSED = 'axon-training-elapsed';

// ============================================================================
// TYPES
// ============================================================================

export interface TrainingScenarioSummary {
  id: string;
  title: string;
  titleTr: string;
  difficulty: string;
  category: string;
  timeLimitSeconds: number;
}

export interface TrainingSessionData {
  sessionId: string;
  scenarioId: string;
  briefing: string;
  briefingTr: string;
  objectives: Array<{
    id: string;
    description: string;
    descriptionTr: string;
    weight: number;
  }>;
  timeLimitSeconds: number;
  hints: Array<{ trigger: string; message: string; messageTr: string }>;
}

export interface TrainingScoreData {
  sessionId: string;
  scenarioId: string;
  scores: {
    taskCompletion: number;
    efficiency: number;
    correctness: number;
    timeManagement: number;
  };
  overallScore: number;
  feedback: string;
  strengths: string[];
  improvements: string[];
  tips: string[];
  timeSpentSeconds: number;
}

export interface TrainingProgressData {
  completedScenarios: string[];
  scores: Record<string, number>;
  totalScore: number;
  lastSessionAt: string | null;
  streak: number;
}

export interface UseTrainingReturn {
  scenarios: TrainingScenarioSummary[];
  activeSession: TrainingSessionData | null;
  lastScore: TrainingScoreData | null;
  progress: TrainingProgressData | null;
  isLoading: boolean;
  error: string | null;
  elapsedSeconds: number;
  loadScenarios: () => Promise<void>;
  startSession: (scenarioId: string) => Promise<void>;
  submitForScoring: () => Promise<void>;
  loadProgress: () => Promise<void>;
  clearSession: () => void;
  updateElapsedTime: (seconds: number) => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Type guard for TrainingSessionData
 */
function isValidSessionData(value: unknown): value is TrainingSessionData {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj['sessionId'] === 'string' &&
    typeof obj['scenarioId'] === 'string' &&
    typeof obj['briefing'] === 'string'
  );
}

/**
 * Type guard for TrainingScoreData
 */
function isValidScoreData(value: unknown): value is TrainingScoreData {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj['sessionId'] === 'string' &&
    typeof obj['scenarioId'] === 'string' &&
    typeof obj['overallScore'] === 'number'
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useTraining(): UseTrainingReturn {
  const t = useTranslations('chatbot.training');
  const [scenarios, setScenarios] = useState<TrainingScenarioSummary[]>([]);
  const [activeSession, setActiveSession] = useState<TrainingSessionData | null>(() =>
    loadFromStorage(STORAGE_KEY_SESSION, null, isValidSessionData)
  );
  const [lastScore, setLastScore] = useState<TrainingScoreData | null>(() =>
    loadFromStorage(STORAGE_KEY_LAST_SCORE, null, isValidScoreData)
  );
  const [progress, setProgress] = useState<TrainingProgressData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(() =>
    loadFromStorage(STORAGE_KEY_ELAPSED, 0, (v): v is number => typeof v === 'number' && v >= 0)
  );

  // Track elapsed time for persistence
  const sessionStartTimeRef = useRef<number | null>(null);

  // Persist elapsed time periodically
  useEffect(() => {
    if (activeSession && elapsedSeconds > 0) {
      saveToStorage(STORAGE_KEY_ELAPSED, elapsedSeconds);
    }
  }, [activeSession, elapsedSeconds]);

  // Persist activeSession when it changes
  useEffect(() => {
    if (activeSession) {
      saveToStorage(STORAGE_KEY_SESSION, activeSession);
    }
  }, [activeSession]);

  // Persist lastScore when it changes
  useEffect(() => {
    if (lastScore) {
      saveToStorage(STORAGE_KEY_LAST_SCORE, lastScore);
    }
  }, [lastScore]);

  const loadScenarios = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<{ scenarios: TrainingScenarioSummary[] }>(
        '/ai-chatbot/training/scenarios'
      );
      setScenarios(response.data.scenarios);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorLoadingScenarios'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const startSession = useCallback(async (scenarioId: string) => {
    setIsLoading(true);
    setError(null);
    setLastScore(null);
    clearFromStorage(STORAGE_KEY_LAST_SCORE);
    setElapsedSeconds(0);
    clearFromStorage(STORAGE_KEY_ELAPSED);
    try {
      const response = await apiClient.post<TrainingSessionData>('/ai-chatbot/training/start', {
        scenarioId,
      });
      setActiveSession(response.data);
      sessionStartTimeRef.current = Date.now();
      saveToStorage(STORAGE_KEY_SESSION, response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorStartingSession'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const submitForScoring = useCallback(async () => {
    if (!activeSession) {
      setError(t('errorNoActiveSession'));
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post<TrainingScoreData>('/ai-chatbot/training/submit', {
        sessionId: activeSession.sessionId,
      });
      setLastScore(response.data);
      saveToStorage(STORAGE_KEY_LAST_SCORE, response.data);
      setActiveSession(null);
      clearFromStorage(STORAGE_KEY_SESSION);
      clearFromStorage(STORAGE_KEY_ELAPSED);
      sessionStartTimeRef.current = null;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorSubmittingForScoring'));
    } finally {
      setIsLoading(false);
    }
  }, [activeSession, t]);

  const loadProgress = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<TrainingProgressData>('/ai-chatbot/training/progress');
      setProgress(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorLoadingProgress'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const clearSession = useCallback(() => {
    setActiveSession(null);
    setLastScore(null);
    setElapsedSeconds(0);
    clearFromStorage(STORAGE_KEY_SESSION);
    clearFromStorage(STORAGE_KEY_LAST_SCORE);
    clearFromStorage(STORAGE_KEY_ELAPSED);
    sessionStartTimeRef.current = null;
  }, []);

  /**
   * Update elapsed time (called from TrainingPanel timer).
   * Also persists to localStorage for recovery after page refresh.
   */
  const updateElapsedTime = useCallback((seconds: number) => {
    setElapsedSeconds(seconds);
    saveToStorage(STORAGE_KEY_ELAPSED, seconds);
  }, []);

  return {
    scenarios,
    activeSession,
    lastScore,
    progress,
    isLoading,
    error,
    elapsedSeconds,
    loadScenarios,
    startSession,
    submitForScoring,
    loadProgress,
    clearSession,
    updateElapsedTime,
  };
}
